import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import api from '../utils/api';
import { toast } from 'sonner';
import { ArrowLeft, Camera, Settings as SettingsIcon } from 'lucide-react';

const ScreenshotSettings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    enabled_globally: false,
    interval_seconds: 5,
    retention_minutes: 60,
    resolution: '1280x720',
    quality: 20
  });

  useEffect(() => {
    // Fetch settings on mount
    fetchSettings();
  }, []);
  
  // Refetch when component becomes visible again
  useEffect(() => {
    // This runs whenever location or location.key changes (navigation)
    fetchSettings();
  }, [location.key]); // location.key changes on each navigation

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings/screenshots');
      setSettings(response.data);
    } catch (error) {
      toast.error('Failed to load screenshot settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/settings/screenshots', settings);
      toast.success('Screenshot settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save screenshot settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = (checked) => {
    setSettings({ ...settings, enabled_globally: checked });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/settings')}
              className="border-slate-300"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Settings
            </Button>
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-xl flex items-center justify-center">
                <Camera className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Screenshot Settings</h1>
                <p className="text-sm text-slate-500">Configure automatic screenshot recording</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-8">
        <Card className="shadow-sm border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Camera className="w-5 h-5 text-cyan-600" />
              <span>Screenshot Recording Configuration</span>
            </CardTitle>
            <CardDescription>
              Configure automatic screenshot capture for troubleshooting and monitoring
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Global Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <Label className="text-base font-semibold">Enable Screenshot Recording</Label>
                  <p className="text-sm text-slate-500 mt-1">
                    Globally enable or disable automatic screenshot capture on all agents
                  </p>
                </div>
                <Switch
                  checked={settings.enabled_globally}
                  onCheckedChange={handleToggle}
                />
              </div>

              {/* Capture Settings */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800">Capture Settings</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="interval">Capture Interval (seconds)</Label>
                    <Input
                      id="interval"
                      type="number"
                      min="1"
                      max="60"
                      value={settings.interval_seconds}
                      onChange={(e) => setSettings({ ...settings, interval_seconds: parseInt(e.target.value) })}
                      className="border-slate-300"
                      disabled={!settings.enabled_globally}
                    />
                    <p className="text-xs text-slate-500">How often to capture screenshots (recommended: 5s)</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="retention">Retention Period (minutes)</Label>
                    <Input
                      id="retention"
                      type="number"
                      min="10"
                      max="120"
                      value={settings.retention_minutes}
                      onChange={(e) => setSettings({ ...settings, retention_minutes: parseInt(e.target.value) })}
                      className="border-slate-300"
                      disabled={!settings.enabled_globally}
                    />
                    <p className="text-xs text-slate-500">How long to keep screenshots (recommended: 60 min)</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="resolution">Resolution</Label>
                    <select
                      id="resolution"
                      value={settings.resolution}
                      onChange={(e) => setSettings({ ...settings, resolution: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md"
                      disabled={!settings.enabled_globally}
                    >
                      <option value="1280x720">1280×720 (HD)</option>
                      <option value="1920x1080">1920×1080 (Full HD)</option>
                      <option value="2560x1440">2560×1440 (2K)</option>
                    </select>
                    <p className="text-xs text-slate-500">Screenshot capture resolution</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quality">JPEG Quality (%)</Label>
                    <Input
                      id="quality"
                      type="number"
                      min="10"
                      max="100"
                      value={settings.quality}
                      onChange={(e) => setSettings({ ...settings, quality: parseInt(e.target.value) })}
                      className="border-slate-300"
                      disabled={!settings.enabled_globally}
                    />
                    <p className="text-xs text-slate-500">Compression quality (recommended: 20%)</p>
                  </div>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-900 mb-2 text-sm">Important Information:</h4>
                <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                  <li>Screenshots are automatically overwritten after the retention period</li>
                  <li>Only the latest screenshot is displayed on the asset detail page</li>
                  <li>Screenshots are included when tickets are submitted from the agent</li>
                  <li>Storage: ~720 screenshots per hour at 5s interval (~70MB per agent)</li>
                  <li>Privacy: Ensure compliance with GDPR and local regulations</li>
                </ul>
              </div>

              {/* Privacy Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-semibold text-amber-900 mb-2 text-sm flex items-center">
                  <SettingsIcon className="w-4 h-4 mr-2" />
                  Privacy & Compliance
                </h4>
                <p className="text-xs text-amber-800">
                  Screenshot recording should be disclosed in your service agreement.
                  Ensure clients are aware of monitoring activities and comply with
                  applicable data protection regulations.
                </p>
              </div>

              {/* Save Button */}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 shadow-md"
              >
                {saving ? 'Saving...' : 'Save Screenshot Settings'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ScreenshotSettings;
